import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface VenueGalleryProps {
  venueId: string;
}

interface VenueImage {
  id: string;
  image_url: string;
  is_main: boolean;
  sort_order: number;
}

export function VenueGallery({ venueId }: VenueGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { data: images, isLoading } = useQuery({
    queryKey: ["venue-images", venueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venue_images")
        .select("*")
        .eq("venue_id", venueId)
        .order("is_main", { ascending: false })
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as VenueImage[];
    },
    enabled: !!venueId,
  });

  if (isLoading) return <Skeleton className="aspect-[16/9] w-full rounded-lg" />;
  if (!images?.length) return null;

  const total = images.length;

  function navigateCarousel(dir: number) {
    setCurrentIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return total - 1;
      if (next >= total) return 0;
      return next;
    });
  }

  function navigateLightbox(dir: number) {
    if (lightboxIndex === null) return;
    const next = lightboxIndex + dir;
    if (next >= 0 && next < total) setLightboxIndex(next);
  }

  return (
    <>
      {/* Main carousel */}
      <div className="relative overflow-hidden rounded-lg">
        <div
          className="cursor-pointer"
          onClick={() => setLightboxIndex(currentIndex)}
        >
          <img
            src={images[currentIndex].image_url}
            alt={`Foto ${currentIndex + 1}`}
            className="aspect-[16/9] w-full object-cover"
          />
        </div>

        {/* Navigation arrows */}
        {total > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/60 backdrop-blur-sm hover:bg-background/80 h-8 w-8"
              onClick={() => navigateCarousel(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/60 backdrop-blur-sm hover:bg-background/80 h-8 w-8"
              onClick={() => navigateCarousel(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}

        {/* Counter badge */}
        {total > 1 && (
          <span className="absolute bottom-3 right-3 rounded-full bg-background/80 px-2.5 py-1 text-xs font-medium backdrop-blur-sm">
            {currentIndex + 1} / {total}
          </span>
        )}

        {/* Dots */}
        {total > 1 && total <= 8 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                className={`h-1.5 w-1.5 rounded-full transition-all ${
                  i === currentIndex
                    ? "bg-foreground w-3"
                    : "bg-foreground/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxIndex !== null} onOpenChange={() => setLightboxIndex(null)}>
        <DialogContent className="max-w-4xl border-none bg-black/95 p-0">
          <div className="relative flex min-h-[50vh] items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 z-10 text-white hover:bg-white/20"
              onClick={() => setLightboxIndex(null)}
            >
              <X className="h-5 w-5" />
            </Button>

            {lightboxIndex !== null && lightboxIndex > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 text-white hover:bg-white/20"
                onClick={() => navigateLightbox(-1)}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {lightboxIndex !== null && (
              <img
                src={images[lightboxIndex].image_url}
                alt="Foto ampliada"
                className="max-h-[80vh] max-w-full object-contain"
              />
            )}

            {lightboxIndex !== null && lightboxIndex < total - 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 text-white hover:bg-white/20"
                onClick={() => navigateLightbox(1)}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}

            {lightboxIndex !== null && (
              <p className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-white/60">
                {lightboxIndex + 1} / {total}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
